import { TriangleAlert } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { Controller, type SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Banner } from 'components/reusable/Banner'
import { BorderedRadioButton, Modal, Tooltip, TooltipProvider } from 'slab'
import { useWindowLocation } from 'utils/windowLocation'

import { TransactionDateInput } from './ReimbursementDateInput'
import { type MemberReimbursementFormInput, ReimbursementPurpose } from './types'
import { formatMoney, getEnglishLanguageDateFromDateString, getEnglishLanguageMonthYearFromDateString } from '../../utils'
import { handleAmountChange } from '../../utils/reimbursement_utils'
import { useCreateReimbursement } from '../hooks/reimbursements/useCreateReimbursement'
import { useGetMinTransactionDate } from '../hooks/reimbursements/useGetMinTransactionDate'
import ReimbursementFileUpload from '../reusable/ReimbursementFileUpload'
import { Button } from '../ui/button'

const InsurancePremiumDescription = 'Premium payments for medical, dental or vision insurance that were paid out of pocket.'

const OtherHealthcareExpenseDescription = (
  <>
    <p className='mb-4'>Healthcare expenses, such as copays, prescriptions, or medical bills.</p>
    <p>Text Thatchbot at 842824 to see <a href='https://support.thatch.com/qualified-medical-expense' target='_blank' rel='noopener noreferrer' className='group-disabled:text-opacity-30'>what products or services are eligible</a>.</p>
  </>
)

const choices: {
  description: React.ReactNode;
  label: string;
  value: string;
  disabled?: boolean;
}[] = [
  {
    description: InsurancePremiumDescription,
    label: 'Insurance premium',
    value: ReimbursementPurpose.PREMIUM
  },
  {
    description: OtherHealthcareExpenseDescription,
    label: 'Other healthcare expense',
    value: ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE
  }
]

interface ICHRAReimbursementProps {
  hasQMEAvailable: boolean,
  maxICHRABalance: number,
  maxICHRABalanceLastYear: number,
}

const ICHRAReimbursement: React.FC<ICHRAReimbursementProps> = ({ hasQMEAvailable, maxICHRABalance, maxICHRABalanceLastYear }) => {
  const { assign } = useWindowLocation()
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false)
  const { data: minTransactionDate, error } = useGetMinTransactionDate()
  const createReimbursementMutation = useCreateReimbursement()

  const updatedChoices = choices.map(choice => {
    if (choice.value === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE) {
      return { ...choice, disabled: !hasQMEAvailable }
    }
    return choice
  })

  const {
    control,
    formState: { errors, isSubmitting, isSubmitted },
    handleSubmit,
    register,
    trigger,
    watch,
    clearErrors,
    setValue
  } = useForm <MemberReimbursementFormInput>({
    mode: 'onTouched',
    defaultValues: {
      amount: '0',
      checkbox_one: false,
      checkbox_two: false,
      checkbox_three: false,
      purpose: !hasQMEAvailable ? ReimbursementPurpose.PREMIUM : '',
      receipts: [],
      transaction_date: '',
      type: 'ICHRA'
    },
  })

  const amount = watch('amount')
  const purpose = watch('purpose')
  const transactionDate = watch('transaction_date')
  const memberDescription = watch('member_description')
  const receipts = watch('receipts')

  const getCorrectBalance = () => {
    if (transactionDate) {
      const date = new Date(transactionDate).toISOString()
      const year = new Date(date).getUTCFullYear()
      const currentYear = new Date().getFullYear()
      return year === currentYear || year < currentYear - 1 ? maxICHRABalance : maxICHRABalanceLastYear
    } else {
      return maxICHRABalance
    }
  }

  const getMaximumAmount = () => {
    if (purpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE) {
      return getCorrectBalance()
    }
  }

  const [reimbursementLimit, setReimbursementLimit] = useState<number | undefined>(getMaximumAmount())

  useEffect(() => {
    setReimbursementLimit(getMaximumAmount())
  }, [purpose, transactionDate])

  const handleOpenDialogue = async () => {
    await trigger()
    if (!Object.keys(errors).length) {
      setOpenConfirmDialog(true)
    }
  }

  const onSubmit: SubmitHandler<MemberReimbursementFormInput> = async (data) => {
    const formData = new FormData()
    formData.append('amount', data.amount.replace(/,/g, ''))
    formData.append('member_description', data.member_description)
    formData.append('purpose', data.purpose)
    formData.append('transaction_date', data.transaction_date)
    formData.append('type', data.type)
    for (const receipt of data.receipts) {
      formData.append('receipts[]', receipt)
    }

    await createReimbursementMutation.mutateAsync(formData)
      .then((response) => {
        assign(response.redirect_to)
      })
      .catch((error: Error) => {
        setOpenConfirmDialog(false)
        toast.error(error.message, { dismissible: true, icon: <TriangleAlert className='w-5 h-5 text-yellow-400' />, position: 'top-right' })
      })
  }

  const isButtonDisabled = !watch('checkbox_one') || !watch('checkbox_two') || !watch('checkbox_three') ||
    !amount || amount === '0' || amount.trim() === '' ||
    !memberDescription || memberDescription.trim() === '' ||
    !receipts || receipts.length === 0 || Object.keys(errors).length > 0

  if (error) {
    return (
      <div className='mt-6'>
        <Banner type='critical' titleText='Something went wrong. Please try again.' />
      </div>
    )
  }

  return (
    <div className='mt-6'>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name='purpose' control={control} rules={{ required: true }} render={({ field: { onChange } }) => (
            <div className='space-y-2'>
              <label
                className='label-default'
                htmlFor='purpose'
              >
                Reimbursement purpose
              </label>
              <div className='space-y-2'>
                <TooltipProvider>
                  {updatedChoices.map((choice) => (
                    <Tooltip
                      content='Your Thatch budget only covers insurance premiums'
                      enabled={!!choice.disabled}
                      key={choice.value}
                      side='bottom'
                    >
                      <div className='focus:outline-none'>
                        <BorderedRadioButton
                          value={choice.value}
                          label={choice.label}
                          description={choice.description}
                          checked={purpose === choice.value}
                          onChange={() => onChange(choice.value)}
                          disabled={!!choice.disabled}
                        />
                      </div>
                    </Tooltip>
                  ))}
                </TooltipProvider>
              </div>
              {errors.purpose && <p className='text-red-500 text-xs mt-2'>This field is required</p>}
            </div>
          )}
        />
        {
          purpose && minTransactionDate && (
            <>
              <Controller
                name='transaction_date'
                control={control}
                render={() => (
                  <div className='mt-6'>
                    <TransactionDateInput<MemberReimbursementFormInput>
                      minDate={minTransactionDate}
                      register={register}
                      purpose={purpose}
                      transactionDate={transactionDate}
                      setValue={setValue}
                    />
                    {errors.transaction_date && <p className='text-red-500 text-xs mt-2'>{errors.transaction_date.message}</p>}
                  </div>
                )}
              />

              <>
                <Controller
                  name='amount'
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => {
                      if (purpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE) {
                        const numericValue = parseFloat(value.replace(/,/g, ''))
                        return numericValue <= (reimbursementLimit || 0) || 'Amount cannot exceed your available balance'
                      }
                      return true
                    }
                  }}
                  render={({ field }) => (
                    <div className='mt-6 space-y-2'>
                      <label className='label-default'>
                        Amount
                        <input
                          data-testid='amount'
                          className='input-default mt-2'
                          type='text'
                          inputMode='numeric'
                          {...field}
                          onChange={(e) => { handleAmountChange(e, field.onChange, reimbursementLimit) }}
                        />
                        <div className='text-xs text-gray-400 mt-2'>
                          {purpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE ? `Your available balance is ${formatMoney(reimbursementLimit || maxICHRABalance)}` : 'Monthly premium cost'}
                        </div>
                      </label>
                      {errors.amount && <p className='text-red-500 text-xs mt-2'>{errors.amount.message || 'This field is required'}</p>}
                    </div>
                  )}
                />
                <div className='mt-6 space-y-2'>
                  <label className='label-default'>
                    {purpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE ? 'Merchant or provider name' : 'Insurance company'}
                    <input
                      className='input-default mt-2'
                      type='text'
                      {...register('member_description', { required: true })}
                    />
                  </label>
                  {errors.member_description && <p className='text-red-500 text-xs mt-2'>This field is required</p>}
                </div>
                <div className='mt-10 mb-6 space-y-2'>
                  <h2 className='mb-1 h2'>Receipts</h2>
                  <div className='label-default'>
                    <div>
                      <div className='font-normal text-gray-700'>
                        <div className='mb-1'>Your itemized receipt must show all of the following:</div>
                        <ul className='list-disc pl-4 my-4'>
                          <li>Name of the provider or business (person or company you paid)</li>
                          <li>Transaction date (when you paid)</li>
                          <li>Itemized description of goods or services purchased</li>
                          <li>Amount paid</li>
                          <li>Form of payment (ex: credit card or ACH)</li>
                        </ul>
                        <div className='mb-4'>If you have a letter of medical necessity (LMN), make sure to attach it alongside your receipt.</div>
                      </div>
                    </div>
                    <Controller
                      name='receipts'
                      control={control}
                      defaultValue={[]}
                      rules={{ required: true }}
                      render={({ field: { onChange, value } }) => (
                        <ReimbursementFileUpload
                          files={value}
                          handleFilesChange={(newFiles) => {
                            onChange(newFiles)
                            // Reset the form's error state for receipts
                            clearErrors('receipts')
                          }}
                          accept='image/png, image/jpeg, image/heic, application/pdf'
                          multiple
                          supportedFormats={['PNG', 'JPG', 'HEIC', 'PDF']}
                        />
                      )}
                    />
                  </div>
                  {errors.receipts && <p className='text-red-500 text-xs mt-2'>This field is required</p>}
                </div>
                <hr />
                <div className='mt-6 space-y-4'>
                  <label className='label-default'>
                    Confirm the following
                  </label>
                  <div>
                    <label className='flex items-center'>
                      <input
                        type='checkbox'
                        className='checkbox-default'
                        {...register('checkbox_one', { required: true })}
                      />
                      <span className='text-sm text-gray-700'>This expense is for me or a family member</span>
                    </label>
                    {errors.checkbox_one && <p className='text-red-500 text-xs mt-2'>This field is required</p>}
                  </div>

                  <div>
                    <label className='flex items-center'>
                      <input
                        type='checkbox'
                        className='checkbox-default'
                        {...register('checkbox_two', { required: true })}
                      />
                      <span className='text-sm text-gray-700'>This expense happened while I was covered by my employer&apos;s Thatch plan</span>
                    </label>
                    {errors.checkbox_two && <p className='text-red-500 text-xs mt-2'>This field is required</p>}
                  </div>
                  <div>
                    <label className='flex items-center'>
                      <input
                        type='checkbox'
                        className='checkbox-default'
                        {...register('checkbox_three', { required: true })}
                      />
                      <span className='text-sm text-gray-700'>This expense hasn&apos;t already been reimbursed and I won&apos;t reimburse it elsewhere</span>
                    </label>
                    {errors.checkbox_three && <p className='text-red-500 text-xs mt-2'>This field is required</p>}
                  </div>
                </div>
              </>
              {Object.keys(errors).length > 0 && (
                <div className='mt-4'>
                  <Banner
                    type='critical'
                    titleText='Please fix any errors before submitting.'
                  />
                </div>
              )}
              <Button
                className='w-full mt-6'
                disabled={isButtonDisabled}
                onClick={handleOpenDialogue}
                type='button'
              >
                Submit
              </Button>
            </>
          )
        }
      </form>
      <Modal open={openConfirmDialog} onOpenChange={setOpenConfirmDialog}>
        <Modal.Body>
          <Modal.Title>Does this look right?</Modal.Title>
          <Modal.Description>
            <div className='flex flex-col space-y-8 my-3 w-full text-gray-700'>
              <div>
                {purpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE && (
                  <>
                    <hr className='mb-5' />
                    <div className='text-lg mb-1'>Reimbursement</div>
                  </>
                )}
                <div className='grid grid-cols-1 gap-6 pt-4'>
                  <div className='flex flex-col items-start'>
                    <span className='text-sm text-gray-500 mb-1'>Purpose</span>
                    <span className='w-full text-sm font-semibold text-gray-800 text-wrap [overflow-wrap:anywhere]'>{purpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE ? 'Other healthcare expense' : 'Insurance premium'}</span>
                  </div>
                  <div className='flex flex-col items-start'>
                    <span className='text-sm text-gray-500 mb-1'>{purpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE ? 'Merchant or provider' : 'Insurance company'}</span>
                    <span className='w-full text-sm font-semibold text-gray-800 text-wrap [overflow-wrap:anywhere]'>{memberDescription}</span>
                  </div>
                  <div className='flex flex-col items-start'>
                    <span className='text-sm text-gray-500 mb-1'>Amount</span>
                    <span className='w-full text-sm font-semibold text-gray-800 text-wrap [overflow-wrap:anywhere]'>{formatMoney(parseFloat(amount.replace(/,/g, '')))}</span>
                  </div>
                  <div className='flex flex-col items-start'>
                    <span className='text-sm text-gray-500 mb-1'>{purpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE ? 'Date' : 'Month of coverage'}</span>
                    <span className='w-full text-sm font-semibold text-gray-800 text-wrap [overflow-wrap:anywhere]'>
                      {transactionDate && (
                        purpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE
                          ? getEnglishLanguageDateFromDateString(transactionDate)
                          : getEnglishLanguageMonthYearFromDateString(transactionDate)
                      )}
                    </span>
                  </div>
                </div>
              </div>
              {
                purpose === ReimbursementPurpose.QUALIFIED_MEDICAL_EXPENSE && (
                  <div>
                    <hr className='mb-5' />
                    <div className='text-lg mb-1'>Thatch balance</div>
                    <div className='space-y-4 pt-4'>
                      <div className='flex justify-between'>
                        <div className='text-sm text-gray-500'>Current</div>
                        <div className='text-sm text-gray-500'>{formatMoney(getCorrectBalance())}</div>
                      </div>
                      <div className='flex justify-between'>
                        <div className='text-sm text-gray-500'>Reimbursement amount</div>
                        <div className='text-sm text-gray-500'>-{formatMoney(parseFloat(amount.replace(/,/g, '')))}</div>
                      </div>
                      <div className='flex justify-between'>
                        <div className='text-sm font-bold'>Remaining</div>
                        <div className='text-sm font-bold'>{formatMoney(Math.max(getCorrectBalance() - parseFloat(amount.replace(/,/g, '')), 0))}</div>
                      </div>
                    </div>
                  </div>
                )
              }

              <Button
                disabled={isSubmitting || isSubmitted}
                onClick={async () => {
                  await handleSubmit(onSubmit)()
                }}
                type='submit'
              >
                {isSubmitting || isSubmitted ? 'Submitting...' : ' Yes, submit'}
              </Button>
            </div>
          </Modal.Description>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default ICHRAReimbursement
